import { proStudioUrqlGql as urqlGql } from '@pypestream/api-services';
import { throwErrorWithId } from '@pypestream/utils';

import { CombinedErrorInternalObject } from '../xstate/prostudio.api-service.types';

export interface FetchVersionFileContentArgs {
  projectId: string;
  versionId: string;
  filePath: string;
}

/**
 * Reads a single file's content out of the Hasura `getVersionFile` action.
 *
 * The upstream action delivers the payload through TWO different response
 * channels depending on the state of the requested version:
 *
 * 1. Success channel — `response.data.getVersionFile: string`.
 *    Used when the version is already compiled (i.e. a deployed / published
 *    version listed on the Versions page). The Hasura schema declares this
 *    field as `String!`, so a healthy published response always lands here.
 *
 * 2. Legacy error channel —
 *    `response.error.graphQLErrors[0].extensions.internal.response.body.data.file_data`.
 *    Used for draft / uncompiled versions (the "live" content edited on the
 *    Workflows page). The upstream backend wraps the file body inside a thrown
 *    error and Hasura forwards it under `extensions.internal`. This is an
 *    intentional pattern — not a bug — and `checkForErrors` would mistake the
 *    payload for a real error, which is why this helper does the extraction
 *    by hand instead of going through `checkForErrors`.
 *
 * Strategy: try the success channel first (schema-conformant, also the only
 * channel populated for published versions), fall back to the legacy error
 * channel (the only channel populated for drafts), and fail loud via
 * `throwErrorWithId` only when the field is genuinely absent in BOTH channels.
 * Failing loud is important because an earlier implementation silently pushed
 * `undefined` into the download list, which JSZip turned into a zero-byte file
 * with no error surfaced to the user (PSTUDIO-3038: empty `bot.csv` on the
 * Versions page).
 *
 * Note the nullish (`!= null`) check rather than a truthy one: the backend
 * returns `file_data: ""` (HTTP 200) for files that exist but are empty — e.g.
 * an uncompiled `services/*.py` artifact on a draft version. An empty string
 * is real, valid content and must be returned as-is; treating it as a failure
 * made a single empty file reject the whole `Promise.all` and abort the entire
 * ZIP download.
 */
export const fetchVersionFileContent = async ({
  projectId,
  versionId,
  filePath,
}: FetchVersionFileContentArgs): Promise<string> => {
  const response = await urqlGql.getVersionFile({
    projectId,
    versionId,
    filePath,
  });

  // Use a nullish (not truthy) check: the backend returns `file_data: ""`
  // (HTTP 200) for files that exist but are empty — e.g. an uncompiled
  // `services/*.py` artifact on a draft version. An empty string is valid
  // content, so only an absent field (`undefined`) counts as "no content".
  const fromSuccessPath = response.data?.getVersionFile;
  if (fromSuccessPath != null) {
    return fromSuccessPath;
  }

  const fromErrorPath = (
    response.error?.graphQLErrors?.[0]?.extensions?.internal as
      | CombinedErrorInternalObject
      | undefined
  )?.response?.body?.data?.file_data;
  if (fromErrorPath != null) {
    return fromErrorPath;
  }

  throwErrorWithId(`Failed to fetch version file content: ${filePath}`);
};
